import { useCallback, useEffect, useState } from 'react';
import type { AppSession, MeView, PatientDetail } from '../../lib/types';
import { goalLabel } from '@heartcoach/care';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtBp, fmtDateTime, fmtSchedule, pct } from '../../lib/format';
import { threadHash } from '../../lib/routes';
import { Icon } from '../../components/HeartMark';
import { Avatar, Card, Eyebrow, Progress } from '../../components/Bits';
import { BpChart } from '../../components/BpChart';
import { ActivityList, GoalList, GoalsMet } from '../../components/Goals';

/** One patient: clinical detail (redacted for a manager), goals, activity, trend, meds, alerts, the conversation, and the composer. */
export function PatientDetailPage({ session, me, patientId, onChanged, say }: { session: AppSession; me: MeView; patientId: string; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<'message' | 'reminder'>('message');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await api.practicePatient(patientId, session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load this patient.');
    }
  }, [patientId, session.token]);
  useEffect(() => {
    void load().then(() => onChanged());
    // onChanged refreshes the unread count once the doctor has opened this patient; it need not re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const back = me.role === 'doctor' ? { href: '#/practice/patients', label: 'All patients' } : { href: '#/practice', label: 'Patient list' };
  if (error)
    return (
      <>
        <a className="quiet small" href={back.href}>
          <Icon name="back" size={16} /> {back.label}
        </a>
        <p className="form-error">{error}</p>
      </>
    );
  if (!detail) return <p className="hint">Loading…</p>;
  const v = detail.view;
  const p = v.progress;

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      if (kind === 'reminder') {
        const at = when ? new Date(when).getTime() : undefined;
        await api.sendReminder(patientId, t, at && at > Date.now() ? at : undefined, session.token);
        say(at && at > Date.now() ? `Reminder scheduled for ${fmtDateTime(at)}.` : 'Reminder sent.');
      } else {
        await api.sendMessage(patientId, t, session.token);
        say('Message sent.');
      }
      setText('');
      setWhen('');
      await load();
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  const ack = async (id: string) => {
    try {
      await api.ackAlert(id, session.token);
      await load();
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not acknowledge that alert.');
    }
  };

  return (
    <>
      <header className="desk-head">
        <a className="quiet small" href={back.href}>
          <Icon name="back" size={16} /> {back.label}
        </a>
        {me.role === 'doctor' ? (
          <a className="button secondary small" href={threadHash(patientId)}>
            <Icon name="chat" size={15} /> Conversation
          </a>
        ) : null}
      </header>
      <div className="detail-head">
        <Avatar name={detail.name} size="lg" />
        <div className="row-main">
          <h1>{detail.name}</h1>
          <span className="hint">
            {detail.age ? `${detail.age} · ` : ''}
            {detail.diagnoses.length ? `${detail.diagnoses.join(', ')} · ` : ''}
            {detail.doctorName} · enrolled {fmtAgo(detail.enrolledAt)}
            {detail.lastActiveAt ? ` · last active ${fmtAgo(detail.lastActiveAt)}` : ''}
          </span>
          <div className="flags">
            <GoalsMet met={detail.goalsMet} total={detail.goalsTotal} />
            {detail.flags.map((f) => (
              <span key={f.code} className={`badge badge-${f.level === 'urgent' ? 'red' : f.level === 'attention' ? 'gold' : 'soft'}`}>
                {f.text}
              </span>
            ))}
            {detail.flags.length === 0 ? <span className="badge badge-green">No flags</span> : null}
          </div>
        </div>
      </div>

      <div className="stat-grid">
        <Card className="stat-card">
          <span className="hint">Latest BP</span>
          <strong>{fmtBp(detail.latestReading)}</strong>
          <span className="hint">{detail.latestReading ? `${detail.latestReading.class.replace('-', ' ')} · ${fmtAgo(detail.latestReading.at)}` : 'no reading yet'}</span>
        </Card>
        <Card className="stat-card">
          <span className="hint">Week average</span>
          <strong>{fmtBp(detail.weeklyAverage)}</strong>
          {v.systolicChange !== null ? <span className={v.systolicChange <= 0 ? 'good' : 'warn'}>{v.systolicChange <= 0 ? '↓' : '↑'} {Math.abs(v.systolicChange)} vs last week</span> : null}
        </Card>
        <Card className="stat-card">
          <span className="hint">Adherence (7d)</span>
          <strong className={p.adherence.percent >= 90 ? 'good' : 'warn'}>{pct(p.adherence.percent)}</strong>
          <span className="hint">
            {p.adherence.taken}/{p.adherence.scheduled} doses
          </span>
        </Card>
        <Card className="stat-card">
          <span className="hint">Engagement</span>
          <strong>{detail.engagement}</strong>
          <span className="hint">
            🔥 {detail.streak} · {detail.coins} coins
          </span>
        </Card>
      </div>

      <div className="two-col">
        <div className="stack">
          <Card>
            <h2>Goals this week</h2>
            <GoalList goals={detail.goals} />
          </Card>
          <Card>
            <h2>Blood pressure, 7 days</h2>
            <BpChart trend={v.trend} />
            {me.role === 'doctor' && v.latestReading?.symptoms.some((s) => s !== 'none') ? <p className="warn">Reported symptoms: {v.latestReading.symptoms.filter((s) => s !== 'none').join(', ')}</p> : null}
          </Card>
          <Card>
            <h2>This week</h2>
            <div className="goal">
              <div className="goal-row">
                <span>Readings</span>
                <span>
                  {p.bp.done}/{p.bp.target}
                </span>
              </div>
              <Progress value={p.bp.done} max={p.bp.target} />
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Step goal days</span>
                <span>{p.steps.daysComplete}/7</span>
              </div>
              <Progress value={p.steps.daysComplete} max={7} tone="green" />
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Adherence</span>
                <span>{pct(p.adherence.percent)}</span>
              </div>
              <Progress value={p.adherence.percent} />
            </div>
          </Card>
          <Card>
            <h2>Medications</h2>
            {v.medications.length === 0 ? <p className="hint">No medications on the plan yet.</p> : null}
            {v.medications.map((m) => (
              <div className="row" key={m.id}>
                <span className="dot-red" aria-hidden="true" />
                <span className="row-main">
                  <span className="row-title">{m.name}</span>
                  <span className="row-sub">
                    {fmtSchedule(m.times)}
                    {m.anticoagulant ? ' · anticoagulant' : ''}
                  </span>
                </span>
                <span className="hint">
                  {v.today.filter((d) => d.medicationId === m.id && d.taken).length}/{m.times.length} today
                </span>
              </div>
            ))}
            <Eyebrow>Care goals</Eyebrow>
            <ul className="plain">
              {v.goals.map((g, i) => (
                <li key={i}>{`${goalLabel(g).title} ${goalLabel(g).target}`}</li>
              ))}
            </ul>
          </Card>
        </div>
        <div className="stack">
          <Card>
            <h2>Send to {detail.name.split(' ')[0]}</h2>
            <div className="segmented">
              <button type="button" className={kind === 'message' ? 'on' : ''} onClick={() => setKind('message')}>
                Message
              </button>
              <button type="button" className={kind === 'reminder' ? 'on' : ''} onClick={() => setKind('reminder')}>
                Reminder
              </button>
            </div>
            <textarea value={text} rows={3} maxLength={1000} placeholder={kind === 'reminder' ? 'Take your evening Metoprolol at 6 PM.' : 'How are you feeling after the dose change?'} onChange={(e) => setText(e.target.value)} />
            {kind === 'reminder' ? (
              <label className="field">
                <span>Deliver at (optional)</span>
                <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              </label>
            ) : null}
            <button type="button" className="primary" disabled={busy || !text.trim()} onClick={() => void send()}>
              {busy ? 'Sending…' : kind === 'reminder' ? (when ? 'Schedule reminder' : 'Send reminder now') : 'Send message'}
            </button>
          </Card>
          <Card>
            <h2>Recent activity</h2>
            <ActivityList items={detail.recentActivity} />
          </Card>
          <Card>
            <h2>Alerts</h2>
            {detail.alerts.length === 0 ? <p className="hint">None raised.</p> : null}
            <ul className="alerts">
              {detail.alerts.map((a) => (
                <li key={a.id} className={`alert alert-${a.level} ${a.ackedAt ? 'acked' : ''}`}>
                  <Icon name="alert" size={16} />
                  <span className="row-main">
                    <span>{a.text}</span>
                    <span className="hint">
                      {fmtAgo(a.at)}
                      {a.ackedAt ? ' · acknowledged' : ''}
                    </span>
                  </span>
                  {!a.ackedAt ? (
                    <button type="button" className="quiet small" onClick={() => void ack(a.id)}>
                      Ack
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
          <Card>
            <h2>Conversation</h2>
            {detail.messages.length === 0 ? <p className="hint">No messages either way yet.</p> : null}
            <ul className="thread">
              {detail.messages.map((m) => (
                <li key={m.id} className={m.from.role === 'patient' ? 'from-patient' : ''}>
                  <span className="row-main">
                    <span>{m.text}</span>
                    <span className="hint">
                      {m.from.role === 'patient' ? `${m.from.name} wrote` : `${m.kind === 'reminder' ? 'Reminder' : 'Message'} from ${m.from.name}`} ·{' '}
                      {m.deliveredAt ? fmtAgo(m.deliveredAt) : m.scheduledAt ? `scheduled for ${fmtDateTime(m.scheduledAt)}` : 'pending'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}
